import React from 'react';
import { useTheme } from '../../contexts/ThemeContext.jsx';
import ParrotStatusIndicator from '../ParrotStatusIndicator.jsx';

/**
 * Parrot table component with sorting, pagination, and selection
 */
const ParrotTable = ({
  parrots,
  loading,
  selectedParrot,
  onParrotSelect,
  pagination,
  onPageChange,
  onSort,
  onViewRevisions,
  onViewKBStatus
}) => {
  const { theme } = useTheme();
  /**
   * Handle sort column click
   */
  const handleSort = (column) => {
    const currentSort = column;
    const newOrder = currentSort === column && pagination.sortOrder === 'asc' ? 'desc' : 'asc';
    onSort(column, newOrder);
  };

  /**
   * Format date for display
   */
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  /**
   * Get status badge styling
   */
  const getStatusBadge = (status) => {
    const baseClasses = 'px-2 py-1 rounded-full text-xs font-medium';
    
    switch (status?.toLowerCase()) {
      case 'active':
        return `${baseClasses} ${
          theme === 'dark' 
            ? 'bg-green-900/30 text-green-400 border border-green-500/30' 
            : 'bg-green-100 text-green-800 border border-green-200'
        }`;
      case 'inactive':
        return `${baseClasses} ${
          theme === 'dark' 
            ? 'bg-red-900/30 text-red-400 border border-red-500/30' 
            : 'bg-red-100 text-red-800 border border-red-200'
        }`;
      case 'draft':
        return `${baseClasses} ${
          theme === 'dark' 
            ? 'bg-yellow-900/30 text-yellow-400 border border-yellow-500/30' 
            : 'bg-yellow-100 text-yellow-800 border border-yellow-200'
        }`;
      default:
        return `${baseClasses} ${
          theme === 'dark' 
            ? 'bg-gray-900/30 text-gray-400 border border-gray-500/30' 
            : 'bg-gray-100 text-gray-800 border border-gray-200'
        }`;
    }
  };

  /**
   * Render sort icon
   */
  const SortIcon = ({ column, currentSort, sortOrder }) => {
    if (currentSort !== column) {
      return <span className={`${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'} ml-1`}>↕️</span>;
    }
    return (
      <span className={`${theme === 'dark' ? 'text-teal-400' : 'text-teal-600'} ml-1`}>
        {sortOrder === 'asc' ? '↑' : '↓'}
      </span>
    );
  };

  /**
   * Render pagination controls
   */
  const PaginationControls = () => {
    const { page, totalPages } = pagination;
    const pages = [];
    
    // Calculate page range to show
    const startPage = Math.max(1, page - 2);
    const endPage = Math.min(totalPages, page + 2);
    
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return (
      <div className={`flex items-center justify-between px-4 py-3 ${
        theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'
      } border-t`}>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => onPageChange(page - 1)}
            disabled={page <= 1}
            className={`px-3 py-1 text-sm rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
              theme === 'dark' 
                ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
                : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
            }`}
          >
            Previous
          </button>
          
          {startPage > 1 && (
            <>
              <button
                onClick={() => onPageChange(1)}
                className={`px-3 py-1 text-sm rounded transition-colors ${
                  theme === 'dark' 
                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                }`}
              >
                1
              </button>
              {startPage > 2 && <span className={`${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>...</span>}
            </>
          )}
          
          {pages.map(pageNum => (
            <button
              key={pageNum}
              onClick={() => onPageChange(pageNum)}
              className={`px-3 py-1 text-sm rounded transition-colors ${
                pageNum === page
                  ? 'bg-teal-600 text-white'
                  : theme === 'dark'
                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
              }`}
            >
              {pageNum}
            </button>
          ))}
          
          {endPage < totalPages && (
            <>
              {endPage < totalPages - 1 && <span className={`${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>...</span>}
              <button
                onClick={() => onPageChange(totalPages)}
                className={`px-3 py-1 text-sm rounded transition-colors ${
                  theme === 'dark' 
                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                }`}
              >
                {totalPages}
              </button>
            </>
          )}
          
          <button
            onClick={() => onPageChange(page + 1)}
            disabled={page >= totalPages}
            className={`px-3 py-1 text-sm rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
              theme === 'dark' 
                ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
                : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
            }`}
          >
            Next
          </button>
        </div>
        
        <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
          Page {page} of {totalPages}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className={`${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-lg border shadow-sm`} data-theme={theme}>
        <div className="p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-400 mx-auto"></div>
          <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} mt-4`}>Loading parrots...</p>
        </div>
      </div>
    );
  }

  if (!parrots || parrots.length === 0) {
    return (
      <div className={`${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-lg border shadow-sm`} data-theme={theme}>
        <div className="p-8 text-center">
          <div className="text-4xl mb-4">🦜</div>
          <h3 className={`text-lg font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-2`}>No parrots found</h3>
          <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
            No parrots match your current filters. Try adjusting your search criteria.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-lg border overflow-hidden shadow-sm`} data-theme={theme}>
      {/* Table header */}
      <div className={`px-4 py-3 ${theme === 'dark' ? 'bg-gray-900 border-gray-700' : 'bg-gray-50 border-gray-200'} border-b`}>
        <h3 className={`text-lg font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Parrots ({pagination.total})</h3>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className={`w-full ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'}`}>
          <thead className={`${theme === 'dark' ? 'bg-gray-900 border-gray-700' : 'bg-gray-50 border-gray-200'} border-b`}>
            <tr>
              <th 
                className={`px-4 py-3 text-left text-xs font-medium ${
                  theme === 'dark' ? 'text-gray-300 hover:text-white' : 'text-gray-700 hover:text-gray-900'
                } uppercase tracking-wider cursor-pointer transition-colors`}
                onClick={() => handleSort('name')}
              >
                Name
                <SortIcon column="name" currentSort={pagination.sortBy} sortOrder={pagination.sortOrder} />
              </th>
              <th 
                className={`px-4 py-3 text-left text-xs font-medium ${
                  theme === 'dark' ? 'text-gray-300 hover:text-white' : 'text-gray-700 hover:text-gray-900'
                } uppercase tracking-wider cursor-pointer transition-colors`}
                onClick={() => handleSort('user_name')}
              >
                Owner
                <SortIcon column="user_name" currentSort={pagination.sortBy} sortOrder={pagination.sortOrder} />
              </th>
              <th className={`px-4 py-3 text-left text-xs font-medium ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
              } uppercase tracking-wider`}>
                Status
              </th>
              <th className={`px-4 py-3 text-left text-xs font-medium ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
              } uppercase tracking-wider`}>
                Phone Numbers
              </th>
              <th className={`px-4 py-3 text-left text-xs font-medium ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
              } uppercase tracking-wider`}>
                Knowledge Base
              </th>
              <th 
                className={`px-4 py-3 text-left text-xs font-medium ${
                  theme === 'dark' ? 'text-gray-300 hover:text-white' : 'text-gray-700 hover:text-gray-900'
                } uppercase tracking-wider cursor-pointer transition-colors`}
                onClick={() => handleSort('updated_at')}
              >
                Last Updated
                <SortIcon column="updated_at" currentSort={pagination.sortBy} sortOrder={pagination.sortOrder} />
              </th>
              <th className={`px-4 py-3 text-left text-xs font-medium ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
              } uppercase tracking-wider`}>
                Actions
              </th>
            </tr>
          </thead>
          <tbody className={`${theme === 'dark' ? 'divide-gray-700 bg-gray-800' : 'divide-gray-200 bg-white'} divide-y`}>
            {parrots.map((parrot) => (
              <tr
                key={parrot.id}
                className={`cursor-pointer transition-colors ${
                  selectedParrot?.id === parrot.id
                    ? theme === 'dark'
                      ? 'bg-teal-900/20 border-l-4 border-l-teal-400'
                      : 'bg-teal-50 border-l-4 border-l-teal-500'
                    : theme === 'dark'
                      ? 'hover:bg-gray-700/50'
                      : 'hover:bg-gray-50'
                }`}
                onClick={() => onParrotSelect(parrot)}
              >
                <td className="px-4 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-8 w-8">
                      <div className="h-8 w-8 rounded-full bg-purple-600 flex items-center justify-center">
                        <span className="text-sm font-medium text-white dark:text-white">
                          🦜
                        </span>
                      </div>
                    </div>
                    <div className="ml-3">
                      <div className={`text-sm font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                        {parrot.name || 'Unnamed Parrot'}
                      </div>
                      <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                        ID: {parrot.id}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-4 whitespace-nowrap">
                  <div className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                    {parrot.user_name || parrot.user_email || 'Unknown User'}
                  </div>
                  <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                    {parrot.user_email}
                  </div>
                </td>
                <td className="px-4 py-4 whitespace-nowrap">
                  <span className={getStatusBadge(parrot.status || 'active')}>
                    {parrot.status || 'Active'}
                  </span>
                </td>
                <td className="px-4 py-4 whitespace-nowrap">
                  <div className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                    {parrot.phone_numbers?.length || 0} numbers
                  </div>
                  {parrot.phone_numbers?.length > 0 && (
                    <div className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                      {parrot.phone_numbers.slice(0, 2).map(phone => phone.phone_number).join(', ')}
                      {parrot.phone_numbers.length > 2 && ` +${parrot.phone_numbers.length - 2} more`}
                    </div>
                  )}
                </td>
                <td className="px-4 py-4 whitespace-nowrap">
                  <div className="flex items-center space-x-2">
                    <ParrotStatusIndicator
                      parrotId={parrot.id}
                      parrotName={parrot.name}
                      size="small"
                      showProgress={true}
                      showTooltip={true}
                      onClick={(event, data) => {
                        event.stopPropagation();
                        if (onViewKBStatus) {
                          onViewKBStatus(parrot);
                        }
                      }}
                    />
                    <div>
                      <div className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                        {parrot.rag_files?.length || 0} files
                      </div>
                      {parrot.rag_files?.length > 0 && (
                        <div className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                          {parrot.rag_files.slice(0, 2).map(rag => rag.name).join(', ')}
                          {parrot.rag_files.length > 2 && ` +${parrot.rag_files.length - 2} more`}
                        </div>
                      )}
                    </div>
                  </div>
                </td>
                <td className={`px-4 py-4 whitespace-nowrap text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                  {formatDate(parrot.updated_at)}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onViewRevisions(parrot);
                    }}
                    className={`${theme === 'dark' ? 'text-teal-400 hover:text-teal-300' : 'text-teal-600 hover:text-teal-700'} mr-3`}
                  >
                    History
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && <PaginationControls />}
    </div>
  );
};

export default ParrotTable;
